import React, { useMemo, useState, useRef, useEffect } from "react";
import Papa from "papaparse";
import axios from "axios";
import { PrimaryButton } from "@fluentui/react";
import { Stack } from "@fluentui/react";
import {
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
} from "@fluentui/react/lib/DetailsList";
import { TextField } from "@fluentui/react/lib/TextField";
import config from "../../config";
import Markdown from "markdown-to-jsx";

const CSVProcessor: React.FC = () => {
  interface CsvRow {
    question: string;
    "config id": string;
    url: string;
    date?: string;
    res_answer?: string;
    time_to_answer?: string;
    res_sql?: string;
    res_definitions?: string;
    [key: string]: string | undefined;
  }

  const MINIMUM_KEYS = ["question", "config id", "url", "summary"];
  const STANDARD_KEYS = [
    ...MINIMUM_KEYS,
    "time_to_answer",
    "sql_failed",
    "sql_passed",
    "tools_called",
    "is_sql_agent_called",
    "is_sql_agent_error_attempt1",
    "is_sql_agent_error_attempt2",
    "is_sql_table_no_datapoints",
    "is_convo_agent_asks_clarification",
  ];
  const [data, setData] = useState<CsvRow[]>([]);
  const [fileName, setFileName] = useState<string>("results_new.csv");
  const [loading, setLoading] = useState<boolean>(false);
  const [stopProcessing, setStopProcessing] = useState<boolean>(false);
  const [editRow, setEditRow] = useState<number | null>(null);
  const [editColumn, setEditColumn] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [viewMode, setViewMode] = useState<"minimum" | "standard" | "full">(
    "minimum"
  );

  // Use a ref to have an up-to-date value inside the sequential loop
  const stopProcessingRef = useRef(stopProcessing);
  useEffect(() => {
    stopProcessingRef.current = stopProcessing;
  }, [stopProcessing]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (
        result: Papa.ParseResult<{
          question: string;
          "config id": string;
          url: string;
        }>
      ) => {
        if (
          !result.meta.fields?.includes("question") ||
          !result.meta.fields?.includes("config id") ||
          !result.meta.fields?.includes("url")
        ) {
          alert("CSV must contain 'question', 'config id', and 'url' columns.");
          return;
        }
        setData(result.data);
      },
    });
  };

  const processCSV = async () => {
    if (data.length === 0) {
      alert("No data to process. Please upload a valid CSV file.");
      return;
    }
    setLoading(true);
    setStopProcessing(false);

    // Process rows sequentially
    for (let i = 0; i < data.length; i++) {
      // Check if user has requested to stop processing
      if (stopProcessingRef.current) {
        console.log("Processing stopped by user.");
        break;
      }

      const startTime = Date.now();
      let updatedRow = data[i];

      try {
        const response = await axios.post(`${config.api.baseUrl}/api/process`, {
          question: data[i]["question"],
          configId: data[i]["config id"],
          url: data[i]["url"],
        });
        const endTime = Date.now();
        const timeToAnswer = ((endTime - startTime) / 1000).toFixed(2);
        const resultData = response.data.result;

        let resSql = resultData.res_sql ?? {};
        if (typeof resSql === "string") {
          try {
            resSql = JSON.parse(resSql);
          } catch (error) {
            console.error("Failed to parse res_sql:", error);
            resSql = {};
          }
        }

        // Flatten res_sql into key-value pairs for dynamic columns
        const flattenedResSql: Record<string, any> = {};
        if (resSql && typeof resSql === "object") {
          Object.entries(resSql).forEach(([key, value]) => {
            if (typeof value === "object" && value !== null) {
              Object.entries(value).forEach(([subKey, subValue]) => {
                flattenedResSql[`technical_${key}_${subKey}`] = subValue;
              });
            } else {
              flattenedResSql[`technical_${key}`] = value;
            }
          });
        }

        updatedRow = {
          ...data[i],
          date: new Date().toISOString(),
          summary: JSON.stringify(resultData.res_answer) || "",
          time_to_answer: timeToAnswer,
          res_sql: JSON.stringify(resultData.res_sql?.sql_cmd) || "",
          sql_question: JSON.stringify(resultData.res_sql?.sql_question) || "",
          definitions_sql_agent:
            JSON.stringify(resultData.res_definitions) || "",
          api_duration: resultData.duration || "",
          // Adding in calculated columns
          is_sql_agent_called: ((resultData?.debug_info?.sql_failed || 0) + (resultData?.debug_info?.sql_passed || 0)) > 0 ? "true" : "false",
          is_sql_agent_error_attempt1: resultData?.debug_info?.sql_failed > 0 ? "true" : "false",
          is_sql_agent_error_attempt2: resultData?.debug_info?.sql_failed > 1 ? "true" : "false",
          is_sql_table_no_datapoints: (JSON.stringify(resultData.res_answer) || "").toLowerCase().includes("no data") ? "true" : "false",
          is_convo_agent_asks_clarification: (JSON.stringify(resultData?.debug_info?.tool_calls_list) || "").endsWith('"ask_for_clarification","with_definitions"]') ? "true" : "false",
          //
          method_called: resultData?.debug_info?.method_called || "",
          sql_failed: resultData?.debug_info?.sql_failed || 0,
          sql_passed: resultData?.debug_info?.sql_passed || 0,
          tools_called:
            JSON.stringify(resultData?.debug_info?.tool_calls_list) || "",
          definitions_convo_agent:
            JSON.stringify(resultData?.debug_info?.definitions_to_use) || "",
          sql_attempts:
            JSON.stringify(resultData?.debug_info?.sql_attempts) || "",
          ...flattenedResSql,
        };
      } catch (error) {
        const endTime = Date.now();
        const timeToAnswer = ((endTime - startTime) / 1000).toFixed(2);
        console.error("Request failed:", error);
        updatedRow = {
          ...data[i],
          date: new Date().toISOString(),
          summary: `Request failed: ${JSON.stringify(error)}`,
          time_to_answer: timeToAnswer,
          res_sql: "",
          definitions_sql_agent: "",
        };
      }

      // Update the processed row in state
      setData((prevData) => {
        const newData = [...prevData];
        newData[i] = updatedRow;
        return newData;
      });
    }
    setLoading(false);
  };

  const duplicateRow = (index: number) => {
    const newData = [...data];
    newData.splice(index + 1, 0, { ...data[index] });
    setData(newData);
  };

  const deleteRow = (index: number) => {
    const newData = data.filter((_, i) => i !== index);
    setData(newData);
  };

  const columns: IColumn[] = useMemo(() => {
    if (data.length === 0) return [];
    const allKeys = new Set<string>();
    data.forEach(row => {
      Object.keys(row).forEach(key => allKeys.add(key));
    });
  
    const keysToInclude = new Set<string>();
    switch (viewMode) {
      case "minimum":
        MINIMUM_KEYS.forEach(k => keysToInclude.add(k));
        break;
      case "standard":
        STANDARD_KEYS.forEach(k => keysToInclude.add(k));
        break;
      case "full":
        allKeys.forEach(k => keysToInclude.add(k));
        break;
    }
  
    const dynamicColumns = Array.from(allKeys)
      .filter(key => keysToInclude.has(key))
      .map(key => ({
        key,
        name: key,
        fieldName: key,
        minWidth: 100,
        maxWidth: 200,
        isMultiline: true,
        isResizable: true,
        onRender: (item: CsvRow, index?: number, column?: IColumn) => {
          if (!column || index === undefined) return null;
          const value = item[column.fieldName ?? ""] || "";
          const isEditing = editRow === index && editColumn === column.fieldName;
  
          if (isEditing) {
            return (
              <TextField
                value={editValue}
                onChange={(e, newValue) => setEditValue(newValue || "")}
                onBlur={() => {
                  const newData = [...data];
                  if (column.fieldName) {
                    newData[index][column.fieldName] = editValue;
                  }
                  setData(newData);
                  setEditRow(null);
                  setEditColumn(null);
                }}
                autoFocus
              />
            );
          }
  
          if (["question", "config id", "url"].includes(key)) {
            return (
              <div
                onDoubleClick={() => {
                  setEditRow(index);
                  if (column.fieldName) {
                    setEditColumn(column.fieldName);
                  }
                  setEditValue(value);
                }}
              >
                {value}
              </div>
            );
          }
  
          if (key === "res_answer") {
            const markdownContent = value.replace(/\\n/g, '\n');
            return (
              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                <Markdown>{markdownContent}</Markdown>
              </div>
            );
          }
  
          return <span>{value}</span>;
        },
      }));
  
    return [
      ...dynamicColumns,
      {
        key: "duplicate",
        name: "Duplicate",
        fieldName: "duplicate",
        minWidth: 100,
        onRender: (_, index) =>
          index !== undefined && (
            <PrimaryButton onClick={() => duplicateRow(index)}>Duplicate</PrimaryButton>
          ),
      },
      {
        key: "delete",
        name: "Delete",
        fieldName: "delete",
        minWidth: 100,
        onRender: (_, index) =>
          index !== undefined && (
            <PrimaryButton onClick={() => deleteRow(index)}>Delete</PrimaryButton>
          ),
      },
    ];
  }, [data, editRow, editColumn, editValue, viewMode]);
  

  const downloadCSV = () => {
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Stack
      tokens={{ childrenGap: 20, padding: 20 }}
      styles={{ root: { padding: 20 } }}
    >
      <input type="file" accept=".csv" onChange={handleFileUpload} />
      {data.length > 0 && (
        <>
          <PrimaryButton onClick={processCSV} disabled={loading}>
            {loading ? "Processing..." : "Process CSV"}
          </PrimaryButton>
          {/* Show Stop Processing button only while processing */}
          {loading && (
            <PrimaryButton onClick={() => setStopProcessing(true)}>
              Stop Processing
            </PrimaryButton>
          )}
          <PrimaryButton onClick={downloadCSV}>Download CSV</PrimaryButton>
          <div>
            <label>
              <input
                type="radio"
                name="viewMode"
                value="minimum"
                checked={viewMode === "minimum"}
                onChange={() => setViewMode("minimum")}
              />
              Minimum
            </label>
            <label style={{ marginLeft: 20 }}>
              <input
                type="radio"
                name="viewMode"
                value="standard"
                checked={viewMode === "standard"}
                onChange={() => setViewMode("standard")}
              />
              Standard
            </label>
            <label style={{ marginLeft: 20 }}>
              <input
                type="radio"
                name="viewMode"
                value="full"
                checked={viewMode === "full"}
                onChange={() => setViewMode("full")}
              />
              Full
            </label>
          </div>

          <div style={{ overflowY: "auto", maxHeight: "80vh" }}>
            <DetailsList
              items={data}
              columns={columns}
              setKey="set"
              selectionMode={0}
              layoutMode={DetailsListLayoutMode.fixedColumns}
              selectionPreservedOnEmptyClick={true}
            />
          </div>
        </>
      )}
    </Stack>
  );
};

export default CSVProcessor;
